/**
 * Pure adapter functions: DayDetailOut (API) -> ExerciseCardModel[] (UI).
 *
 * No JSX here — this is the data layer for the session detail redesign.
 * Logic ports the approved prototype
 * (docs/plans/assets/2026-07-23-session-detail-prototype.html) onto the real
 * API types instead of its static fixture.
 */

import type {
  CardioDetailOut,
  DayDetailOut,
  ExerciseTrend,
  FreeWeightSetOut,
  SessionDeviceRoundOut,
  TrendVerdict,
} from '../../api/types'
import { BASE_URL } from '../../api/client'
import type { SetRow } from './SetGrid'

export type ImportedSource = 'Technogym' | 'Lanista' | 'Milon'

/**
 * The badge for one card's sets, or undefined for none.
 *
 * Only an origin outside Groundwork gets one. A set logged in the app
 * (`app_live`, `app_web`) carries none, and neither does a source this build
 * does not know. The card used to fall back to "Lanista" for both, which put
 * the name of a platform nobody reads any more (ADR 0012) on every set a user
 * logged here. A card groups every set of one exercise and a day merges
 * sources (`api/set_precedence.py`), so its sets can disagree: a badge true of
 * only some of them is the same defect, so a mixed card carries none. The iOS
 * twin is `ImportedSource(sets:)` in GroundworkKit.
 */
export function importedSource(
  sources: readonly (string | null | undefined)[],
): ImportedSource | undefined {
  const [first] = sources
  if (sources.some((s) => s !== first)) return undefined
  switch (first) {
    case 'technogym':
      return 'Technogym'
    case 'lanista':
      return 'Lanista'
    case 'milon':
      return 'Milon'
    default:
      return undefined
  }
}

export interface ExerciseCardModel {
  key: string
  name: string
  /** The outside platform the card's data was imported from, which the card
   *  names in a badge. Absent for anything logged in Groundwork: no badge. */
  source?: ImportedSource
  imageUrl: string | null
  imageFit: 'cover' | 'contain'
  primary: string[]
  secondary: string[]
  /** label: 'load' | 'reps' */
  trend?: { verdict: TrendVerdict; label: string }
  body:
    | { kind: 'sets'; rows: SetRow[]; idxLabel: 'Set' | 'Round' }
    | { kind: 'metrics'; items: [string, string, string][] }
  /** The planned exercise this one stood in for that day (#267), if any. */
  standsInFor?: string
}

/**
 * The server's name key (`movement_key`): whitespace collapsed and case
 * FOLDED. JavaScript has no casefold, and lowercasing keeps "ß" where
 * Python's casefold makes it "ss", so "Fußheben" and "FUSSHEBEN" were two
 * exercises here and one on the server (#267 review). "ß" is the case-folding
 * difference that occurs in this app's names; the iOS twin is
 * `ExerciseNameKey` and uses Foundation's full case folding.
 */
function nameKey(name: string): string {
  return name.trim().split(/\s+/).join(' ').toLowerCase().replace(/ß/g, 'ss')
}

const MILON_BIKE_DEVICE_ID = 501

/**
 * Prefer the `load` trend verdict; fall back to `reps` only when load is
 * steady but reps actually moved (mirrors the prototype's `lbl` choice).
 */
function pickTrend(
  t: ExerciseTrend | undefined,
  targetsEased = false,
): { verdict: TrendVerdict; label: string } | undefined {
  // A day an adjustment eased (PC-I17), the comeback first: its load change
  // is a planned ease, not a decline, so no chip, and no reps chip in its
  // place either.
  if (!t || targetsEased) return undefined
  if (t.load.direction === 'steady' && t.reps.direction !== 'steady') {
    return { verdict: t.reps, label: 'reps' }
  }
  return { verdict: t.load, label: 'load' }
}

/** First non-empty muscle list among a group of items, or []. */
function firstNonEmpty(lists: (string[] | undefined)[]): string[] {
  for (const l of lists) {
    if (l && l.length > 0) return l
  }
  return []
}

// ── Cardio ──────────────────────────────────────────────────────────

export function buildCardioModels(day: DayDetailOut): ExerciseCardModel[] {
  return day.cardio.map((c: CardioDetailOut, i: number) => {
    const first = c.exercises[0]
    const items: [string, string, string][] = []

    if (c.total_duration_sec != null) {
      items.push(['Duration', String(Math.round(c.total_duration_sec / 60)), 'min'])
    }
    // Distance is a machine-reported "virtual" figure — intentionally omitted.
    const avgHr = first?.avg_hr
    if (avgHr != null) {
      items.push(['Avg HR', String(Math.round(avgHr)), 'bpm'])
    }
    const energy = c.energy_kcal ?? c.total_calories
    if (energy != null) {
      items.push(['Energy', String(Math.round(energy)), 'kcal'])
    }

    return {
      key: `cardio-${c.id_cr ?? i}`,
      name: c.machine ?? 'Cardio',
      source: 'Technogym',
      imageUrl: c.device_image ? BASE_URL + c.device_image : null,
      imageFit: 'cover',
      primary: c.primary_muscles ?? [],
      secondary: c.secondary_muscles ?? [],
      body: { kind: 'metrics', items },
    }
  })
}

// ── Free weights (the app's own log, imported Lanista, merged Technogym strength) ──

export function buildFreeWeightModels(day: DayDetailOut): ExerciseCardModel[] {
  const sets = day.free_weight_workout?.sets ?? []
  if (sets.length === 0) return []

  // Preserve first-seen order by position.
  const orderedByPosition = [...sets].sort((a, b) => a.position - b.position)
  const order: string[] = []
  const groups = new Map<string, FreeWeightSetOut[]>()
  for (const s of orderedByPosition) {
    if (!groups.has(s.exercise_name)) {
      groups.set(s.exercise_name, [])
      order.push(s.exercise_name)
    }
    groups.get(s.exercise_name)!.push(s)
  }

  return order.map((name) => {
    const groupSets = groups.get(name)!
    const sorted = [...groupSets].sort((a, b) => a.set_number - b.set_number)
    const rows: SetRow[] = sorted.map((s) => ({
      label: String(s.set_number).padStart(2, '0'),
      reps: s.reps,
      weight: s.weight_kg,
      durationSec: s.duration_sec ?? null,
      metric: s.metric ?? 'reps',
      repsRight: s.reps_right ?? null,
      durationSecRight: s.duration_sec_right ?? null,
      // The metric guard matters: a hold has weight_kg 0 rather than NULL so
      // it was never marked skipped, but a right-only hold WOULD be, and
      // dimming a real set to 40% opacity is the same defect one column over.
      skipped: s.weight_kg == null && s.metric !== 'seconds',
    }))

    const first = groupSets[0]
    const source = importedSource(groupSets.map((s) => s.source))
    const trend = pickTrend(day.trends.by_exercise[name.toLowerCase()], day.trends.targets_eased)

    const standIn = (day.stand_ins ?? []).find((s) => nameKey(s.name) === nameKey(name))

    return {
      key: `fw-${name}`,
      name,
      source,
      imageUrl: first.image_path ? BASE_URL + first.image_path : null,
      imageFit: 'cover',
      primary: firstNonEmpty(groupSets.map((s) => s.primary_muscles)),
      secondary: firstNonEmpty(groupSets.map((s) => s.secondary_muscles)),
      trend,
      body: { kind: 'sets', rows, idxLabel: 'Set' },
      standsInFor: standIn?.stands_in_for,
    }
  })
}

// ── Milon circuit ─────────────────────────────────────────────────────

export function buildMilonModels(day: DayDetailOut): ExerciseCardModel[] {
  const devices = day.milon?.devices ?? []
  if (devices.length === 0) return []

  const order: number[] = []
  const groups = new Map<number, SessionDeviceRoundOut[]>()
  for (const d of devices) {
    if (d.device_id === MILON_BIKE_DEVICE_ID) continue
    if (!groups.has(d.device_id)) {
      groups.set(d.device_id, [])
      order.push(d.device_id)
    }
    groups.get(d.device_id)!.push(d)
  }

  return order.map((deviceId) => {
    const groupRounds = groups.get(deviceId)!
    const sorted = [...groupRounds].sort((a, b) => a.round_number - b.round_number)
    // Milon rounds carry a duration in the database, but they are rep-scored
    // (D10) — rendering them as timed would change 463 rows of history nobody
    // asked to change. So these stay constant.
    const rows: SetRow[] = sorted.map((r) => ({
      label: `R${r.round_number}`,
      reps: r.reps ?? null,
      weight: r.weight_kg ?? null,
      durationSec: null,
      metric: 'reps',
      repsRight: null,
      durationSecRight: null,
      skipped: r.weight_kg == null,
    }))

    const first = groupRounds[0]
    const trend = pickTrend(day.trends.by_device[String(deviceId)], day.trends.targets_eased)

    return {
      key: `milon-${deviceId}`,
      name: first.device_name,
      source: 'Milon',
      imageUrl: `/images/devices/device_${deviceId}.png`,
      imageFit: 'contain',
      primary: firstNonEmpty(groupRounds.map((r) => r.primary_muscles)),
      secondary: firstNonEmpty(groupRounds.map((r) => r.secondary_muscles)),
      trend,
      body: { kind: 'sets', rows, idxLabel: 'Round' },
    }
  })
}

// ── Summary hero ────────────────────────────────────────────────────

export function buildSummary(day: DayDetailOut): {
  totalLoad: number
  energy: number
  sets: number
  heldSec: number
  primary: string[]
  secondary: string[]
} {
  const freeWeightModels = buildFreeWeightModels(day)
  const milonModels = buildMilonModels(day)
  const cardioModels = buildCardioModels(day)
  const strengthModels = [...freeWeightModels, ...milonModels]

  // The day's load and set count are the server's (api/training_volume.py),
  // never a local sum. A browser sum of reps x weight counted one side of every
  // unilateral set, so this hero read 14,285 kg on production where the one
  // calculation reads more -- and the dashboard, summing something else again,
  // read less for a whole week. Same rule as energy and held time below.
  const totalLoad = day.volume?.tonnage_kg ?? 0
  const sets = day.volume?.working_sets ?? 0

  // From the server, never a local sum: the server applies C-D4's per-side
  // doubling, and a local sum would have this summary disagreeing with the
  // coach and the finish sheet about the same session.
  const heldSec = day.free_weight_workout?.total_seconds_held ?? 0

  // THE single day energy is computed server-side (full-HR live session → its
  // estimate; otherwise device-reported total). Render that one number — never
  // a local sum, never live.energy_kcal on its own.
  const energy = day.energy_kcal ?? 0

  const primary = new Set<string>()
  const secondary = new Set<string>()
  for (const card of [...cardioModels, ...strengthModels]) {
    card.primary.forEach((m) => primary.add(m))
    card.secondary.forEach((m) => secondary.add(m))
  }
  primary.forEach((m) => secondary.delete(m))

  return {
    totalLoad,
    energy,
    sets,
    heldSec,
    primary: [...primary],
    secondary: [...secondary],
  }
}
